import * as R from 'ramda';
import React, { useEffect, useRef, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';

import ExpandableContent from '@ui/expandableContent.tsx';
import { Icon } from '@ui/icons/Icon.tsx';
import { formatDirectionsDistance } from '@utils/distance.ts';
import { getContainerHeight } from '@utils/mapElement.ts';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';
import AriaAnnouncements from '../../../ariaAnnouncer/src/ariaAnnouncements.tsx';
import { EnrouteControls } from './EnRouteControls.tsx';
import MultiStopStepList from './multiStopStepList.tsx';
import {
  RouteInfo,
  ButtonsContainer,
  StepIconWrapper,
  MobileStandardButton,
  MobileStandardSecondaryButton,
  EndNavigationButton,
  guidanceButtonTypography,
} from './styles.ts';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  height: auto;
`;
const RouteInfoStyled = styled(RouteInfo)`
  padding: 0 12px;
`;

const ButtonsContainerStyled = styled(ButtonsContainer)`
  margin-bottom: 12px;
  overflow: visible;
`;

const FlatButtonSmall = styled(MobileStandardButton)`
  flex: 0 1 40px;
  fill: var(--color-primary);
`;

const MultiStopListStyled = styled(MultiStopStepList)`
  overflow: visible;
  width: 100%;
  flex: 1 1 auto;
`;

const PrimaryButton = styled(MobileStandardButton)`
  ${guidanceButtonTypography}
  width: 130px;
  height: 40px;
`;

export const InnerLayout = React.memo(
  ({
    className,
    endpoints,
    times,
    distances,
    steps,
    searchInputs,
    currentStop,
    currentStep,
    onPrevStepClick,
    onNextStepClick,
    isOpen,
    setIsOpen,
    isNavigating,
    fullRoutePreviewEnabled = false,
    toggleIsNavigating,
    onStepClicked,
    notifyAboutChange,
    toggleNavigationHeader,
    onEndRouteClick,
    onEditRouteClick,
    onEndNavigationClick,
    preferredUnits,
    locale,
    T,
    containerRef,
  }) => {
    const colors = useCSSColors();
    const isLastStep = currentStop === steps.length - 1 && currentStep === steps.at(-1).length - 1;
    const showCloseButton = fullRoutePreviewEnabled && !!onEndNavigationClick && isLastStep;
    useEffect(() => {
      setIsOpen(false);
    }, [isNavigating]); // when entering/exiting navigation, collapse bottom bar

    useEffect(() => {
      if (isNavigating) {
        toggleNavigationHeader(!isOpen);
      }
      notifyAboutChange();
    }, [isOpen]); // when content is expanded during navigation - toggle navigation header

    // Navigation buttons (Prev/Next/List)
    const buttonsContainerRef = useRef(null);
    const [isButtonsWrapped, setIsButtonsWrapped] = useState(false);
    const previousWrappedStateRef = useRef(false);

    // Pre-navigation buttons (Steps/Go)
    const preNavButtonsContainerRef = useRef(null);
    const [isPreNavButtonsWrapped, setIsPreNavButtonsWrapped] = useState(false);
    const previousPreNavWrappedStateRef = useRef(false);

    // Focus management refs for Navigation buttons
    const prevStepButtonRef = useRef(null);
    const nextStepButtonRef = useRef(null);
    const stepsToggleButtonRef = useRef(null);
    const lastActivatedNavRef = useRef(null);

    // When entering navigation mode, move focus to the Next button
    useEffect(() => {
      if (isNavigating) {
        requestAnimationFrame(() => {
          nextStepButtonRef.current?.focus();
        });
      }
    }, [isNavigating]);

    // FRP uses the sheet handle instead of a separate Steps button.
    const prevIsOpenRef = useRef(isOpen);
    useEffect(() => {
      const wasStepsPanelOpen = prevIsOpenRef.current;
      prevIsOpenRef.current = isOpen;

      if (wasStepsPanelOpen && !isOpen && isNavigating) {
        requestAnimationFrame(() => {
          const toggle = fullRoutePreviewEnabled
            ? containerRef?.current?.closest('[data-expandable-content]')?.querySelector('[data-testid="PillToggle"]')
            : stepsToggleButtonRef.current;
          toggle?.focus({ preventScroll: true });
        });
      }
    }, [isOpen, isNavigating, fullRoutePreviewEnabled, containerRef]);

    // Unified button wrap detection for both navigation and pre-navigation states
    useLayoutEffect(() => {
      // Select the appropriate container and state based on navigation mode
      const container = isNavigating ? buttonsContainerRef.current : preNavButtonsContainerRef.current;
      const previousRef = isNavigating ? previousWrappedStateRef : previousPreNavWrappedStateRef;
      const setWrapped = isNavigating ? setIsButtonsWrapped : setIsPreNavButtonsWrapped;

      if (!container) {
        return;
      }

      const measureButtonsHeight = () => {
        // Get the actual content height including wrapped content
        const contentHeight = container.scrollHeight;
        // Single row should be ~40px (button height) + 12px (margin-bottom)
        const expectedSingleRowHeight = 52;

        const wrapped = contentHeight > expectedSingleRowHeight;

        // Only update state and notify if wrap state changed
        if (wrapped !== previousRef.current) {
          setWrapped(wrapped);
          previousRef.current = wrapped;
          notifyAboutChange();
        }
      };

      // Measure immediately
      measureButtonsHeight();

      // Listen for window resize events
      window.addEventListener('resize', measureButtonsHeight);

      return () => {
        window.removeEventListener('resize', measureButtonsHeight);
      };
    }, [isNavigating, notifyAboutChange]);

    useEffect(() => {
      if (!isNavigating) {
        return;
      }

      const isNextDisabled = isLastStep && !showCloseButton;
      const isPrevDisabled = currentStop === 0 && currentStep === 0;

      // Defer focus logic to the next event loop tick so the browser layout
      // completely processes the re-render
      requestAnimationFrame(() => {
        const navState = lastActivatedNavRef.current;
        if (!navState || typeof navState !== 'object') {
          return;
        }

        const focusTargets = {
          next: !isNextDisabled ? nextStepButtonRef : prevStepButtonRef,
          prev: !isPrevDisabled ? prevStepButtonRef : nextStepButtonRef,
        };

        focusTargets[navState.button]?.current?.focus({ preventScroll: true });

        // Reset ref so focus is only managed on explicit button activations
        lastActivatedNavRef.current = null;
      });
    }, [currentStep, currentStop, isNavigating, isLastStep, showCloseButton]);

    const handlePrevStepClick = e => {
      // e.detail is 0 for keyboard activations (Enter/Space)
      lastActivatedNavRef.current = {
        button: 'prev',
        isKeyboard: e?.detail === 0,
      };
      onPrevStepClick();
    };

    const handleNextStepClick = e => {
      // e.detail is 0 for keyboard activations (Enter/Space)
      lastActivatedNavRef.current = {
        button: 'next',
        isKeyboard: e?.detail === 0,
      };
      onNextStepClick();
    };

    const toggleNavigation = () => {
      setIsOpen(false);
      toggleIsNavigating(!isNavigating);
    };

    const distanceDisplay = formatDirectionsDistance(R.sum(distances), preferredUnits, T);

    const minutesText = R.sum(times)
      ? T('getDirectionsFromTo:_minutes_ min total', { minutes: R.sum(times) })
      : T('getDirectionsFromTo:<1 min');

    const stepsStopsButton =
      steps.length > 1 ? (
        <MobileStandardSecondaryButton onClick={() => setIsOpen(!isOpen)} data-testid="ShowStopsButton">
          <Icon className="icon" width={24} height={24} name="location" cdn fillColor={colors.secondaryButtonText} />
          &nbsp;{T('getDirectionsFromTo:Stops')}
        </MobileStandardSecondaryButton>
      ) : (
        <MobileStandardSecondaryButton onClick={() => setIsOpen(!isOpen)} data-testid="ShowStepsButton">
          <Icon className="icon" width={24} height={24} name="list-view" cdn fillColor={colors.secondaryButtonText} />
          &nbsp;{T('getDirectionsFromTo:Steps')}
        </MobileStandardSecondaryButton>
      );

    return (
      <Wrapper className={className} ref={containerRef} data-testid="DirectionsResultViewMobile">
        <AriaAnnouncements
          announcements={{
            announcementKey: 'directionsStep',
            announcementText: `${steps[currentStop][currentStep]?.secondaryTextUI} ${steps[currentStop][currentStep]?.primaryText}`,
          }}
        />
        {isNavigating && !isOpen ? (
          <RouteInfoStyled>
            <StepIconWrapper>
              <Icon name={steps[currentStop][currentStep].icon} cdn width={32} height={45} />
            </StepIconWrapper>
            <div>
              <span className="label" data-testid="StepLabel">
                {steps[currentStop][currentStep]?.secondaryTextUI}
              </span>
              <span className="info" data-testid="StepInfo">
                {steps[currentStop][currentStep]?.primaryText}
              </span>
            </div>
          </RouteInfoStyled>
        ) : (
          <RouteInfoStyled>
            <StepIconWrapper>
              <Icon name={'wayfinding.end'} cdn width={32} height={45} />
            </StepIconWrapper>
            <div>
              <span className="info" data-testid="InfoPrimary">
                {minutesText}
                {' • '}
                {distanceDisplay}
              </span>
              <span className="label" data-testid="InfoSecondary">
                {T(`getDirectionsFromTo:To ${endpoints[endpoints.length - 1]?.title}`)}
              </span>
            </div>
          </RouteInfoStyled>
        )}

        {!isNavigating && (
          <ButtonsContainerStyled
            $isWrapped={isPreNavButtonsWrapped}
            ref={preNavButtonsContainerRef}
            data-testid="PreNavButtonsContainer"
          >
            {isOpen ? (
              <MobileStandardSecondaryButton onClick={() => setIsOpen(!isOpen)} data-testid="ShowMapButton">
                <Icon className="icon" width={24} height={24} name="map" cdn fillColor={colors.secondaryButtonText} />
                &nbsp;{T('getDirectionsFromTo:Map')}
              </MobileStandardSecondaryButton>
            ) : (
              stepsStopsButton
            )}
            <MobileStandardButton onClick={toggleNavigation} data-testid="GoButton">
              <Icon
                className="icon"
                width={24}
                height={24}
                name="directions-filled"
                cdn
                fillColor={colors.primaryButtonText}
              />
              &nbsp;{T('getDirectionsFromTo:Go')}
            </MobileStandardButton>
          </ButtonsContainerStyled>
        )}

        {isNavigating && (
          <ButtonsContainerStyled
            $isWrapped={isButtonsWrapped}
            ref={buttonsContainerRef}
            data-testid="ButtonsContainerStyled"
          >
            <PrimaryButton
              ref={prevStepButtonRef}
              onClick={handlePrevStepClick}
              disabled={currentStop === 0 && currentStep === 0}
              data-testid="PrevStepButton"
            >
              <Icon
                className="icon"
                width={32}
                height={32}
                name="chevron-left"
                cdn
                fillColor={colors.secondaryButtonText}
              />
              &nbsp;{T('getDirectionsFromTo:Prev')}
            </PrimaryButton>
            {!fullRoutePreviewEnabled && (
              <FlatButtonSmall
                ref={stepsToggleButtonRef}
                onClick={() => setIsOpen(!isOpen)}
                data-testid="StepsToggleButton"
                aria-label={T('getDirectionsFromTo:Toggle Steps')}
                aria-expanded={isOpen}
              >
                <Icon
                  className="icon"
                  width={24}
                  height={24}
                  name="list-view"
                  cdn
                  fillColor={colors.secondaryButtonText}
                />
              </FlatButtonSmall>
            )}
            <PrimaryButton
              ref={nextStepButtonRef}
              type="button"
              onClick={showCloseButton ? onEndNavigationClick : handleNextStepClick}
              disabled={isLastStep && !showCloseButton}
              data-testid={showCloseButton ? 'CloseNavigationButton' : 'NextStepButton'}
            >
              {showCloseButton ? T('ui:Close') : T('getDirectionsFromTo:Next')}
              {!showCloseButton && (
                <Icon
                  className="icon"
                  width={32}
                  height={32}
                  name="chevron-right"
                  cdn
                  fillColor={colors.secondaryButtonText}
                />
              )}
            </PrimaryButton>
          </ButtonsContainerStyled>
        )}
        {isNavigating && isOpen && onEndNavigationClick && !isLastStep && (
          <EndNavigationButton type="button" onClick={onEndNavigationClick}>
            {T('getDirectionsFromTo:End Navigation')}
          </EndNavigationButton>
        )}
        <MultiStopListStyled
          ariaHidden={!isOpen}
          inert={!isOpen}
          legs={steps}
          times={times}
          distances={distances}
          searchInputs={searchInputs}
          onStepClicked={onStepClicked}
          T={T}
          isNavigating={isNavigating}
          currentStop={currentStop}
          currentStep={currentStep}
          className={isOpen ? 'visible' : 'collapsed'}
          mobileView={true}
          locale={locale}
          isOpen={isOpen}
        />
        {isNavigating && !fullRoutePreviewEnabled && (
          <EnrouteControls
            isOpen={!isOpen}
            editRouteLabel={T('getDirectionsFromTo:Edit Route')}
            endRouteLabel={T('getDirectionsFromTo:End Route')}
            onEditRouteClick={onEditRouteClick}
            onEndRouteClick={onEndRouteClick}
          />
        )}
      </Wrapper>
    );
  },
);
const DirectionsResultViewMobile = props => {
  const { T } = props;
  // Calculate max height based on actual container height, not vh units.
  // Uses the SDK entry point (.atrius-map) for measurements so positioning
  // works correctly both with and without an iframe.
  const mapHeight = getContainerHeight();
  const maxHeightPx = props.isNavigating ? `${mapHeight}px` : `${Math.round(mapHeight * 0.66)}px`;

  return (
    <ExpandableContent
      className="DirectionsResultSheet"
      maxHeight={maxHeightPx}
      bus={props.bus}
      isDraggable={true}
      T={T}
      isMapDisabled={!props.isNavigating}
      hasInnerScrollingElement={true}
      allowScrollWhenCollapsed={true}
    >
      <InnerLayout {...props} />
    </ExpandableContent>
  );
};

export default DirectionsResultViewMobile;
